import { APP_CONTEXT } from '@/utils/context';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { useContext, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { NavLink, useLocation } from 'react-router-dom';
import Styled from './side-navigation-style';

const variants = {
  open: {
    height: 'auto',
  },
  close: {
    height: 0,
  },
};

/**
 * Side Navigation component.
 * @param {object} props The props.
 * @param {boolean} props.open The open.
 * @param {Function} props.setOpen The open.
 * @param {object[]} props.navlinks The navlinks.
 * @param {string} props.activeSideNav The activeSideNav.
 * @param {string} props.activeChildSideNav The activeChildSideNav.
 * @param {string} props.secondaryColor The company secondary color.
 * @returns {JSX.Element} The rendered Side Navigation component.
 */
const SideNavigation = ({
  open,
  navlinks,
  activeSideNav,
  activeChildSideNav,
  secondaryColor,
  setOpen,
}) => {
  const { showSideNav } = useContext(APP_CONTEXT);
  const [dropOpen, setDropOpen] = useState('');
  const location = useLocation();
  return (
    <Styled.Navigation
      secondaryColor={secondaryColor}
      open={open}
      $showSideNav={showSideNav}
      $icon
    >
      {navlinks.map((navLink) => {
        if (
          navLink.children &&
          navLink.children.filter((route) => route.tab).length >= 1
        ) {
          if (open) {
            return (
              <Styled.DropDown key={navLink.name}>
                <Styled.DropDownHeader
                  onClick={() =>
                    setDropOpen(
                      dropOpen === navLink.active ? '' : navLink.active,
                    )
                  }
                  className={activeSideNav === navLink.active ? 'active' : ''}
                >
                  <navLink.sidebarIcon className="icon" size={20} />
                  <span data-cy={navLink.cy}>{navLink.name}</span>
                  {dropOpen === navLink.active ? (
                    <FiChevronUp className="exp" />
                  ) : (
                    <FiChevronDown className="exp" />
                  )}
                </Styled.DropDownHeader>
                <Styled.DropDownBody
                  variants={variants}
                  initial="close"
                  animate={dropOpen === navLink.active ? 'open' : 'close'}
                >
                  <Styled.DropDownChildren maxHeight="500px">
                    {navLink.children
                      .filter((route) => route.tab)
                      .map((route) => (
                        <Styled.NavItem
                          key={route.path}
                          open={open}
                          className="child"
                        >
                          <NavLink
                            data-cy={route.cy}
                            onClick={() => setDropOpen('')}
                            to={`${navLink.path}/${route.path}`}
                            className={
                              activeChildSideNav === route.active
                                ? 'activeChild child'
                                : 'child'
                            }
                          >
                            <span>{route.title}</span>
                          </NavLink>
                        </Styled.NavItem>
                      ))}
                  </Styled.DropDownChildren>
                </Styled.DropDownBody>
              </Styled.DropDown>
            );
          }
          return (
            <Styled.NavItem key={navLink.name} open={open}>
              <NavLink
                data-cy={navLink.cy}
                to={navLink.path}
                className={activeSideNav === navLink.active ? 'active' : ''}
              >
                <navLink.sidebarIcon size={20} />
              </NavLink>
            </Styled.NavItem>
          );
        }
        return (
          <Styled.NavItem key={navLink.name} open={open}>
            <NavLink
              data-cy={navLink.cy}
              to={navLink.path}
              onClick={() => setOpen(false)}
              className={location.pathname === navLink.path ? 'active' : ''}
            >
              <navLink.sidebarIcon size={20} />
              {open ? <span>{navLink.name}</span> : null}
            </NavLink>
          </Styled.NavItem>
        );
      })}
    </Styled.Navigation>
  );
};

SideNavigation.propTypes = {
  open: bool.isRequired,
  navlinks: arrayOf(shape({})).isRequired,
  activeSideNav: string.isRequired,
  activeChildSideNav: string.isRequired,
  setOpen: func.isRequired,
  secondaryColor: string.isRequired,
};

export default SideNavigation;
